import {appConfig} from '@/config/appConfig';

/**
 * Application role definitions matching REMUS Keycloak authorities.
 */
export const UserRole = {
    ADMIN: 'ROLE_ADMIN',
    EXPERT_EN: 'ROLE_EXPERT_EN',
    EXPERT_RP: 'ROLE_EXPERT_RP',
    USER_EN: 'ROLE_USER_EN',
    USER_RP: 'ROLE_USER_RP',
    GUEST_EN: 'ROLE_GUEST_EN',
    GUEST_RP: 'ROLE_GUEST_RP',
    REMUS_USER: 'ROLE_REMUS_USER',
    VISUALIZE_TRENDS: 'ROLE_VISUALIZE_TRENDS',
} as const;

export type UserRoleType = (typeof UserRole)[keyof typeof UserRole];

export const ACTIVE_ROLES_CHANGED_EVENT = 'active-roles-changed';

export const RolePriority: Record<UserRoleType, number> = {
    [UserRole.ADMIN]: 1,
    [UserRole.EXPERT_EN]: 2,
    [UserRole.EXPERT_RP]: 2,
    [UserRole.USER_EN]: 3,
    [UserRole.USER_RP]: 3,
    [UserRole.GUEST_EN]: 4,
    [UserRole.GUEST_RP]: 4,
    [UserRole.REMUS_USER]: 5,
    [UserRole.VISUALIZE_TRENDS]: 6,
};

export const RoleNames: Record<UserRoleType, string> = {
    [UserRole.ADMIN]: 'Admin',
    [UserRole.EXPERT_EN]: 'Expert EN',
    [UserRole.EXPERT_RP]: 'Expert RP',
    [UserRole.USER_EN]: 'User EN',
    [UserRole.USER_RP]: 'User RP',
    [UserRole.GUEST_EN]: 'Guest EN',
    [UserRole.GUEST_RP]: 'Guest RP',
    [UserRole.REMUS_USER]: 'REMUS User',
    [UserRole.VISUALIZE_TRENDS]: 'Visualize Trends',
};

export const getActiveRoles = (): UserRoleType[] => {
    try {
        const userConfigurations = localStorage.getItem(appConfig.userConfigurationsKey);
        if (!userConfigurations) return [];
        const parsed = JSON.parse(userConfigurations);
        return Array.isArray(parsed.activeRoles) ? parsed.activeRoles : [];
    } catch (e) {
        console.error('Failed to get active roles from localStorage', e);
        return [];
    }
};

export const setActiveRoles = (roles: UserRoleType[]): void => {
    try {
        const userConfigurations = localStorage.getItem(appConfig.userConfigurationsKey);
        const updatedConfigurations = userConfigurations
            ? {...JSON.parse(userConfigurations), activeRoles: roles}
            : {activeRoles: roles};
        localStorage.setItem(appConfig.userConfigurationsKey, JSON.stringify(updatedConfigurations));
        if (typeof window !== 'undefined') {
            window.dispatchEvent(
                new CustomEvent<UserRoleType[]>(ACTIVE_ROLES_CHANGED_EVENT, {detail: roles})
            );
        }
    } catch (e) {
        console.error('Failed to set active roles in localStorage', e);
    }
};

export const clearActiveRoles = (): void => {
    try {
        const userConfigurations = localStorage.getItem(appConfig.userConfigurationsKey);
        if (userConfigurations) {
            const config = JSON.parse(userConfigurations);
            delete config.activeRoles;
            if (Object.keys(config).length === 0) {
                localStorage.removeItem(appConfig.userConfigurationsKey);
            } else {
                localStorage.setItem(appConfig.userConfigurationsKey, JSON.stringify(config));
            }
        }
        if (typeof window !== 'undefined') {
            window.dispatchEvent(
                new CustomEvent<UserRoleType[]>(ACTIVE_ROLES_CHANGED_EVENT, {detail: []})
            );
        }
    } catch (e) {
        console.error('Failed to clear active roles from localStorage', e);
    }
};

export const areRolesEqual = (roles1: UserRoleType[], roles2: UserRoleType[]): boolean => {
    if (roles1.length !== roles2.length) return false;
    const sorted1 = [...roles1].sort();
    const sorted2 = [...roles2].sort();
    return sorted1.every((role, index) => role === sorted2[index]);
};

export const getHighestPriorityRole = (roles: UserRoleType[]): UserRoleType | null => {
    if (!roles || roles.length === 0) return null;
    return [...roles].sort(
        (a, b) =>
            (RolePriority[a] ?? Number.MAX_SAFE_INTEGER) -
            (RolePriority[b] ?? Number.MAX_SAFE_INTEGER)
    )[0];
};
